import { useRef, useState } from 'react';
import { Button } from '@/ui/Button';
import { Dialog } from '@/ui/Dialog';
import { Field, Input, Select, Textarea } from '@/ui/Field';
import { ORDEN_DE_TIPOS, TIPOS } from './piezas';
import { useCrearRecurso, type TipoDeRecurso } from './api';

/**
 * NuevoRecurso.tsx — la opción 3: subir un recurso a la biblioteca.
 *
 * ## El archivo deja de vivir en `window`
 *
 * El vanilla guardaba el archivo elegido en `window._rcFile`. `Convenciones.md`
 * lo prohíbe por nombre, con un caso real: en Formación un archivo elegido y
 * descartado acababa subiéndose en el siguiente formulario.
 *
 * Aquí había además una variante propia: elegir un archivo, cambiar el tipo a
 * «Enlace» —que esconde el campo del archivo pero no lo vacía— y escribir una
 * URL mandaba **las dos cosas**. Ahora solo viaja lo que corresponde al tipo
 * elegido, y el archivo desaparece con el diálogo.
 *
 * ## Las mismas reglas que el servidor, antes de enviar
 *
 * `create_resource` (`content.py:1231`) rechaza título vacío o de más de 200
 * caracteres, descripción de menos de 20, un tipo fuera del Enum y un enlace
 * que no sea http(s) o lleve comillas. Se comprueban aquí con los mismos
 * límites para que el error salga en el campo, y no como un aviso genérico
 * después de haber subido 50 MB.
 */

const TIPOS_DE_ENLACE: TipoDeRecurso[] = ['enlace', 'video'];
const TOPE_MB = 50;
const MINIMO_DESCRIPCION = 20;
const MAXIMO_TITULO = 200;

export function NuevoRecurso({ abierto, onCerrar }: { abierto: boolean; onCerrar: () => void }) {
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [tipo, setTipo] = useState<TipoDeRecurso>('libro');
  const [enlace, setEnlace] = useState('');
  const [archivo, setArchivo] = useState<File | null>(null);
  const [errores, setErrores] = useState<Record<string, string>>({});
  const crear = useCrearRecurso();
  const refArchivo = useRef<HTMLInputElement>(null);

  const esEnlace = TIPOS_DE_ENLACE.includes(tipo);

  function limpiar() {
    setTitulo(''); setDescripcion(''); setTipo('libro'); setEnlace('');
    setArchivo(null); setErrores({});
  }

  function cerrar() {
    limpiar();
    onCerrar();
  }

  function validar(): boolean {
    const e: Record<string, string> = {};
    const t = titulo.trim();
    if (!t) e.titulo = 'El recurso necesita un título.';
    else if (t.length > MAXIMO_TITULO) e.titulo = `El título no puede pasar de ${MAXIMO_TITULO} caracteres.`;
    if (descripcion.trim().length < MINIMO_DESCRIPCION) {
      e.descripcion = `Describe para qué sirve: mínimo ${MINIMO_DESCRIPCION} caracteres.`;
    }
    if (esEnlace) {
      const u = enlace.trim();
      // El mismo criterio que `_url_segura` en el servidor.
      if (!u) e.enlace = 'Pega el enlace del recurso.';
      else if (!/^https?:\/\//i.test(u)) e.enlace = 'El enlace debe empezar por http:// o https://';
      else if (/["'<>\s]/.test(u)) e.enlace = 'El enlace contiene espacios o caracteres no permitidos.';
    } else if (archivo && archivo.size > TOPE_MB * 1024 * 1024) {
      e.archivo = `El archivo pesa ${(archivo.size / 1024 / 1024).toFixed(1)} MB; el máximo es ${TOPE_MB} MB.`;
    }
    setErrores(e);
    return Object.keys(e).length === 0;
  }

  function enviar() {
    if (!validar()) return;
    crear.mutate(
      {
        campos: {
          title: titulo.trim(),
          description: descripcion.trim(),
          resource_type: tipo,
          ...(esEnlace ? { external_url: enlace.trim() } : {}),
        },
        archivo: esEnlace ? null : archivo,
      },
      {
        onSuccess: () => {
          Utils.toast('Recurso creado', 'success');
          cerrar();
        },
        onError: (err: unknown) =>
          Utils.toast(err instanceof Error ? err.message : 'No se pudo crear el recurso', 'error'),
      },
    );
  }

  const faltan = Math.max(0, MINIMO_DESCRIPCION - descripcion.trim().length);

  return (
    <Dialog
      abierto={abierto}
      onCambio={(v) => !v && cerrar()}
      titulo="Nuevo recurso"
      descripcion="Lo verá todo el portal en la biblioteca."
      ancho="max-w-xl"
      pie={
        <>
          <Button variant="ghost" onClick={cerrar}>Cancelar</Button>
          <Button onClick={enviar} loading={crear.isPending}>
            <i className="bi bi-cloud-upload" aria-hidden="true" /> Crear recurso
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <Field label="Título" error={errores.titulo}>
          <Input
            value={titulo}
            maxLength={MAXIMO_TITULO}
            onChange={(e) => { setTitulo(e.target.value); setErrores((x) => ({ ...x, titulo: '' })); }}
            placeholder="Nombre del recurso"
          />
        </Field>

        <Field
          label="Descripción"
          error={errores.descripcion}
          ayuda={faltan > 0 ? `Faltan ${faltan} caracteres.` : 'Suficiente.'}
        >
          <Textarea
            rows={3}
            value={descripcion}
            onChange={(e) => { setDescripcion(e.target.value); setErrores((x) => ({ ...x, descripcion: '' })); }}
            placeholder="Describe para qué sirve este recurso…"
          />
        </Field>

        <Field label="Tipo">
          <Select
            value={tipo}
            onChange={(e) => { setTipo(e.target.value as TipoDeRecurso); setErrores({}); }}
          >
            {ORDEN_DE_TIPOS.map((k) => (
              <option key={k} value={k}>{TIPOS[k].etiqueta}</option>
            ))}
          </Select>
        </Field>

        {esEnlace ? (
          <Field label="Enlace externo" error={errores.enlace}>
            <Input
              type="url"
              inputMode="url"
              value={enlace}
              onChange={(e) => { setEnlace(e.target.value); setErrores((x) => ({ ...x, enlace: '' })); }}
              placeholder="https://…"
            />
          </Field>
        ) : (
          <Field
            label="Archivo"
            opcional
            id="recurso-archivo"
            error={errores.archivo}
            ayuda={`PDF, imagen o vídeo. Máximo ${TOPE_MB} MB.`}
          >
            {/* `id` y `aria-describedby` a mano: un `<input>` crudo no los
                toma del contexto de `Field`, y sin ellos la etiqueta no queda
                asociada ni se lee la ayuda. */}
            <input
              id="recurso-archivo"
              aria-describedby="recurso-archivo-ayuda"
              aria-invalid={Boolean(errores.archivo)}
              ref={refArchivo}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png,.mp4"
              onChange={(e) => { setArchivo(e.target.files?.[0] ?? null); setErrores((x) => ({ ...x, archivo: '' })); }}
              className="block w-full text-2 file:mr-3 file:min-h-9 pointer-coarse:file:min-h-11
                         file:rounded-sm file:border-0 file:bg-sunken file:px-3 file:text-2
                         file:font-semibold file:text-ink"
            />
          </Field>
        )}
      </div>
    </Dialog>
  );
}
